import React, {useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useProviderSList, useProviderStatusUpdate} from './hooks/useProviderQuery'
import {ToggleState} from './modals/type'

import {DeleteIcon, LockIcon, ViewIcon} from '../../modules/common/TableIcons'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'
import {DeleteModal, PasswordModal} from './modals'
import {
  getConfigurationByKeyAction,
  singleConfigurationSelector,
} from '../../reducers/configurationReducer'
import {useAppDispatch} from '../../store'
import {useSelector} from 'react-redux'
import {statusSelector} from '../../reducers/providerReducer'
import MUITable from '../../modules/common/MUITable'
import {SwitchTextTrack} from '../../../config'
import {makeDate} from '../../../util'

type ProviderRow = {
  profilePic: string
  businessName: string
  status: string
  id: string
  lastLogin: any
  createdAt: any
}

export default function ListTvSeriesProvider() {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const allowDelete: boolean = queryParams.get('super') === 'true'
  const dispatch: any = useAppDispatch()
  const [userData, setUserData] = useState<any>()
  const [id, setId] = useState('')
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [search, setSearch] = useState('')
  type ProviderViewType = 'all' | 'google' | 'others'
  const [providerView, setProviderView] = useState<ProviderViewType>('all')

  const FILTER_FETCH_SIZE = 2000

  const [toggle, setToggle] = useState<ToggleState[]>([
    {state: false, name: 'd-modal'},
    {state: false, name: 'pass-modal'},
  ])

  useEffect(() => {
    dispatch(getConfigurationByKeyAction('provider_approval_flow_bypass'))
    return () => {}
  }, [])

  const configuration = useSelector(singleConfigurationSelector)
  const status = useSelector(statusSelector)

  const {isLoading: statusLoading, mutate} = useProviderStatusUpdate()

  const initTable = {
    columns: [
      {id: 'profilePic', label: '', numeric: false, disablePadding: true},
      {
        id: 'businessName',
        label: 'TV series name',
        numeric: false,
        disablePadding: true,
      },
      {
        id: 'createdAt',
        label: 'Created date',
        numeric: false,
        disablePadding: true,
      },
      {
        id: 'lastLoginAt',
        label: 'Last login',
        numeric: false,
        disablePadding: true,
      },
      {
        id: 'status',
        label: 'Status',
        numeric: false,
        disablePadding: true,
      },
      {
        id: 'action',
        label: 'Action',
        numeric: false,
        disablePadding: true,
      },
    ],
    rows: [],
  }

  function handleOpenModal(modal: string, itemId: string) {
    setToggle((prev) => {
      let modalState = prev.map((curState) => {
        if (curState.name === modal) {
          curState.state = true
          curState.id = itemId
        }
        return curState
      })
      return modalState
    })
  }

  const rowItem = (item: ProviderRow, data: any) => {
    return {
      profilePic: (
        <img
          src={
            item?.profilePic ? item?.profilePic : '/media/icons/duotune/communication/com006.svg'
          }
          alt=''
          className='rounded-circle h-40px w-40px'
        />
      ),
      businessName: item.businessName,
      createdAt: [
        <span className='badge badge-secondary'>
          {item.createdAt ? makeDate(item.createdAt) : 'N/a'}
        </span>,
      ],
      lastLoginAt: [
        <span className='badge badge-secondary'>
          {item.lastLogin ? makeDate(item.lastLogin) : 'N/a'}
        </span>,
      ],
      status: [
        statusLoading || loading ? (
          id === item?.id ? (
            <div className='ms-1'>
              <span className='spinner-border align-middle'></span>
            </div>
          ) : (
            <SwitchTextTrack
              checked={item?.status === 'approved' ? true : false}
            />
          )
        ) : (
          <SwitchTextTrack
            onChange={(e) => {
              if (configuration?.value?.status === 1) {
                mutate({
                  providerId: item?.id,
                  status: item?.status === 'approved' ? 'pending' : 'approved',
                })
                setId(item?.id)
              }
            }}
            disabled={configuration?.value?.status !== 1 ? true : false}
            checked={item?.status === 'approved' ? true : false}
          />
        ),
      ],
      action: [
        <ViewIcon
          path={item.status === 'approved' ? `/tv-series/${item.id}` : ``}
          data={data}
          active={item.status === 'approved'}
        />,
        <LockIcon
          byClick={() => {
            handleOpenModal('pass-modal', item.id)
            setUserData(item)
          }}
          path='#'
        />,
        allowDelete && <DeleteIcon byClick={() => handleOpenModal('d-modal', item.id)} path='#' />,
      ],
    }
  }

  const listSelector = (res: any, view: ProviderViewType) => {
    let providers = res?.providers ?? []
    if (view === 'google') {
      providers = providers.filter((p: any) => p?.googlePlaceId != null && p?.googlePlaceId !== '')
    } else if (view === 'others') {
      providers = providers.filter((p: any) => p?.googlePlaceId == null || p?.googlePlaceId === '')
    }

    if (view === 'all') {
      const rows: never[] = []
      const pagination = {total: res.total, lastPage: res.lastPage}
      providers.forEach((element: any) => {
        const item: ProviderRow = {
          profilePic: element?.profileImage,
          businessName: element?.businessName,
          status: element?.status,
          id: element?.id,
          lastLogin: element?.lastLogin,
          createdAt: element?.createdAt,
        }
        rows.push(rowItem(item, element) as never)
      })
      const table = {...initTable, rows}
      return {table, pagination}
    }

    return {filteredProviders: providers, total: providers.length}
  }

  const {isLoading, data, isFetching} = useProviderSList({
    func: (res: any) => listSelector(res, providerView),
    params: {
      page: providerView === 'all' ? page : 1,
      perPage: providerView === 'all' ? perPage : FILTER_FETCH_SIZE,
      q: search,
      isTvSeries: 1,
    },
  })

  const displayData = useMemo(() => {
    if (!data) return undefined
    if (providerView === 'all' || !('filteredProviders' in data)) {
      return data as {table: typeof initTable; pagination: {total: number; lastPage: number}}
    }
    const {filteredProviders, total} = data as {filteredProviders: any[]; total: number}
    const lastPage = Math.max(1, Math.ceil(total / perPage))
    const start = (page - 1) * perPage
    const slice = filteredProviders.slice(start, start + perPage)
    const rows: never[] = []
    slice.forEach((element: any) => {
      const item: ProviderRow = {
        profilePic: element?.profileImage,
        businessName: element?.businessName,
        status: element?.status,
        id: element?.id,
        lastLogin: element?.lastLogin,
        createdAt: element?.createdAt,
      }
      rows.push(rowItem(item, element) as never)
    })
    return {
      table: {...initTable, rows},
      pagination: {total, lastPage, currentPage: page, entriesPerPage: perPage},
    }
  }, [data, providerView, page, perPage])

  useEffect(() => {
    setLoading(isFetching)
  }, [isFetching])

  return (
    <>
      <div className='row mb-5'>
        <div className='col-12 d-flex justify-content-between align-items-center flex-wrap gap-3'>
          <h2 className='py-3 mb-0'>TV Series</h2>
          <div className='d-flex align-items-center gap-2'>
            <label className='form-label mb-0 text-nowrap'>Provider type:</label>
            <select
              className='form-select form-select-sm'
              style={{width: 'auto', minWidth: '120px'}}
              value={providerView}
              onChange={(e) => {
                setProviderView(e.target.value as ProviderViewType)
                setPage(1)
              }}
            >
              <option value='all'>All</option>
              <option value='google'>Google Providers</option>
              <option value='others'>KnoHow Providers</option>
            </select>
            <input
              type='search'
              onChange={(e) => {
                if (e.target.value.length >= 3) {
                  setSearch(e.target.value)
                }
                if (e.target.value === '') {
                  setSearch(e.target.value)
                }
              }}
              placeholder='Search'
              className='form-control form-control-sm'
              style={{width: '180px'}}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          overflow: 'auto',
          whiteSpace: 'nowrap',
        }}
        className='card mb-5 mb-xl-10 p-5'
      >
        {isLoading ? (
          <TableLoading />
        ) : (
          <MUITable
            data={displayData}
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        )}
      </div>

      {toggle[0].state && <DeleteModal onHide={setToggle} toggle={toggle[0]} />}
      {toggle[1].state && (
        <PasswordModal onHide={setToggle} toggle={toggle[1]} userData={userData} />
      )}
    </>
  )
}
